// T-SQL data-type slot recognizer for the EditorContext projection.
// PRD: GRA-8 (MSSQL projection pivot), design §2.2. G11: pure text scan,
// NO web-tree-sitter — this is the legal projection-side body the future
// tree-sitter swap replaces behind the identical signature.
//
// GRA8-ROLL-1 extracted the six T-SQL data-type shapes out of
// editorContextSlot.ts into this per-engine recognizer file (mirroring the
// per-engine resolver `xxxDataTypes.ts` convention). `resolveSlotKind`
// dispatches `isMssqlDataTypeSlot` only on the `engine === 'mssql'` arm; the
// other 4 engines get their own `isXxxDataTypeSlot` recognizers in later
// slices.
//
// Recognises the six shapes the old text-walk handled, plus the sequence-`AS`
// shape it lacked:
//   1. `CREATE TABLE foo (colName ▮`  — column-list inside CREATE TABLE
//      (persistent, temp `#tmp`, table-variable `DECLARE @t TABLE`).
//   2. `DECLARE @x ▮`                 — scalar local declaration.
//   3. `CAST(expr AS ▮` / `TRY_CAST(expr AS ▮` / `PARSE(expr AS ▮`
//   4. `CONVERT(▮`                    — CONVERT first argument.
//   5. `CREATE PROC/FUNCTION (@param ▮` and the parenless `... @param ▮` header.
//   6. `CREATE SEQUENCE name AS ▮`    — the sequence type slot (NEW).

import type { ClauseToken } from './editorContextClause';
import {
  dropAttachedPartial,
  findEnclosingLparenIndex,
  hasUnclosedLparen,
  isInsideCallTo,
  upper,
} from './editorContextSlotShared';

/** Words that make an enclosing `(` a constraint / partition list rather than a
 *  CREATE TABLE column list. The backward walk in
 *  `isInsideCreateTableColumnList` stops at them, so
 *  `ALTER TABLE t ADD CHECK (price ▮` is not mistaken for a column definition. */
const COLUMN_LIST_BREAK_WORDS: ReadonlySet<string> = new Set([
  'CHECK', 'PRIMARY', 'UNIQUE', 'FOREIGN', 'EXCLUDE', 'PARTITION', 'INDEX', 'KEY',
  'REFERENCES', 'CONSTRAINT', 'VALUES', 'USING',
]);

/**
 * TYPED-PREFIX PARITY: every shape runs against the token stream with the
 * bare word the caret is ATTACHED to DROPPED (`DECLARE @x IN▮` ≡ `DECLARE @x
 * ▮`, `CONVERT(VAR▮` ≡ `CONVERT(▮`) — the half-typed word is the answer, not
 * context. The `AS` arm additionally reads the RAW tail so a caret attached
 * to `AS` itself (`CAST(x AS▮`), which has always fired, keeps firing.
 */
export function isMssqlDataTypeSlot(
  raw: readonly ClauseToken[],
  /** Cursor offset — distinguishes an ATTACHED partial from a ws-separated caret. */
  offset: number,
): boolean {
  const tokens = dropAttachedPartial(raw, offset);
  const prev = tokens[tokens.length - 1];
  if (!prev) return false;

  // Shape 3: ... AS ▮  inside CAST / TRY_CAST / PARSE (PARSE shares the
  // `AS <type>` shape with CAST), OR the top-level CREATE SEQUENCE … AS slot.
  if (upper(prev) === 'AS' || upper(raw[raw.length - 1]) === 'AS') {
    if (hasUnclosedLparen(tokens)) {
      if (isInsideCallTo(tokens, ['CAST', 'TRY_CAST', 'PARSE'])) return true;
    } else if (headIsCreateSequence(tokens)) {
      return true;
    }
  }

  // Shape 4: CONVERT(▮  — cursor sits right after the opening `(` of a CONVERT
  // call (the first arg slot is the data type).
  if (prev.kind === 'lparen') {
    const before = tokens[tokens.length - 2];
    if (before && upper(before) === 'CONVERT') return true;
  }

  // Shape 1: CREATE TABLE column-list (or `#tmp` / `DECLARE @t TABLE`). The
  // prev is a bare column-name identifier sitting at a fresh column slot.
  if (prev.kind === 'identifier' && !prev.text.startsWith('@')) {
    if (isInsideCreateTableColumnList(tokens)) return true;
  }

  // Shapes 2 + 5: `@`-prefixed local, either inside a CREATE PROC/FUNCTION
  // param list, after a parenless procedure header, or after a DECLARE.
  if (prev.kind === 'identifier' && prev.text.startsWith('@')) {
    if (isInsideCreateRoutineParamList(tokens)) return true;
    if (followsCreateProcedureHeader(tokens)) return true;
    if (followsDeclare(tokens)) return true;
  }

  return false;
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a `CREATE TABLE` or `DECLARE @x TABLE`. We look for a `TABLE` keyword in
 * the few tokens preceding the opener and validate the prev (column-name) sits
 * at a fresh column slot (immediately after the `(` or a `,`). The fresh-slot
 * guard keeps `REFERENCES tbl(col ▮` and other mid-clause refs out of the
 * data-type set. Pinned by mssql script.29 §3.
 */
export function isInsideCreateTableColumnList(tokens: readonly ClauseToken[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  let sawTable = false;
  for (let i = lparenIdx - 1; i >= Math.max(0, lparenIdx - 12); i -= 1) {
    const u = upper(tokens[i]);
    if (u === 'TABLE') {
      sawTable = true;
      break;
    }
    const t = tokens[i]!;
    if (t.kind === 'lparen' || t.kind === 'rparen' || t.kind === 'comma') break;
    // A constraint lead means this paren belongs to the constraint, not to a
    // column list: `ALTER TABLE t ADD CHECK (price ▮` is an expression, and
    // `PARTITION BY RANGE (col ▮` a partition list.
    if (u !== null && COLUMN_LIST_BREAK_WORDS.has(u)) break;
  }
  if (!sawTable) return false;
  const beforePrev = tokens[tokens.length - 2];
  if (!beforePrev) return false;
  return beforePrev.kind === 'lparen' || beforePrev.kind === 'comma';
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a CREATE [OR ALTER] PROC[EDURE] / FUNCTION header.
 */
function isInsideCreateRoutineParamList(tokens: readonly ClauseToken[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  const upperHead = tokens
    .slice(0, lparenIdx)
    .map((t) => upper(t))
    .filter((u): u is string => !!u);
  let i = 0;
  if (upperHead[i] !== 'CREATE' && upperHead[i] !== 'ALTER') return false;
  i += 1;
  if (upperHead[i] === 'OR') {
    i += 1;
    if (upperHead[i] !== 'ALTER') return false;
    i += 1;
  }
  return (
    upperHead[i] === 'PROC' ||
    upperHead[i] === 'PROCEDURE' ||
    upperHead[i] === 'FUNCTION'
  );
}

/**
 * True when the cursor follows a `CREATE [OR ALTER] PROC[EDURE]` parenless
 * parameter header (T-SQL stored procedures allow declaring parameters
 * without surrounding parens, e.g. `CREATE PROCEDURE foo @p1 INT, @p2 INT
 * AS ...`). The cursor's `@`-local must appear after the procedure name and
 * before the `AS` keyword that opens the body.
 */
function followsCreateProcedureHeader(tokens: readonly ClauseToken[]): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  let sawProc = false;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = upper(t);
    if (u === 'AS') return false; // Inside body, not header.
    if (t.kind === 'semicolon') return false;
    if (u === 'PROC' || u === 'PROCEDURE') {
      sawProc = true;
      continue;
    }
    if ((u === 'CREATE' || u === 'ALTER') && sawProc) return true;
  }
  return false;
}

/**
 * True when the cursor's most recent DECLARE keyword precedes the `@`-local
 * AND there is no enclosing paren / statement break between them (the scalar-
 * local form, not the `DECLARE @t TABLE(...)` form which is handled by
 * `isInsideCreateTableColumnList`).
 */
function followsDeclare(tokens: readonly ClauseToken[]): boolean {
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = upper(t);
    if (u === 'DECLARE') return true;
    if (
      t.kind === 'lparen' ||
      t.kind === 'semicolon' ||
      u === 'SELECT' ||
      u === 'INSERT' ||
      u === 'UPDATE' ||
      u === 'DELETE' ||
      u === 'BEGIN' ||
      u === 'END'
    ) {
      return false;
    }
  }
  return false;
}

/** True when the statement head is `CREATE [OR ALTER] SEQUENCE`. */
function headIsCreateSequence(tokens: readonly ClauseToken[]): boolean {
  const head = tokens
    .map((t) => upper(t))
    .filter((u): u is string => !!u);
  let i = 0;
  if (head[i] !== 'CREATE') return false;
  i += 1;
  if (head[i] === 'OR') {
    i += 1;
    if (head[i] !== 'ALTER') return false;
    i += 1;
  }
  return head[i] === 'SEQUENCE';
}
